import { FC, FormEvent, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import SharedData from '../../common/SharedData'
import { ICatalogVariantPreview, IFromBarcodePayload, IOfferUpdatePayload } from '../cors/_models'
import { createOfferFromBarcode, getOffer, updateOffer } from '../cors/_requests'
import { toAbsoluteUrl, toCatalogImageUrl } from '../../../../_metronic/helpers'
import AddVariantsPanel from './AddVariantsPanel'
import './variant-multi-offer.css'

/**
 * Multi-variant "Sell Yours": when a barcode maps to several catalog variants
 * (size/color), every variant is a row in one editable table — a unified
 * edit-existing + create-new grid wired to the supplier offer endpoints:
 *
 *   - owned variant (has_my_offer)  → its live offer is fetched
 *     (GET /offers/{psid}) and shown editable; ACTIVE; submit PUTs
 *     updateOffer(/offers/{psid}).
 *   - not-owned variant             → blank editable row; NO OFFER; submit
 *     POSTs createOfferFromBarcode(/offers/from-barcode). Each variant is its
 *     own product_id.
 *
 * Cost is never user-entered — the server derives it from price × supplier
 * margin. We show the offer's real cost for owned rows and a live preview
 * (same formula) while the price is edited / for new rows.
 */

type VStatus = 'idle' | 'saving' | 'done' | 'error'
type VRow = {
  price: string
  qty: string
  sku: string
  psid: number | null
  owned: boolean
  loading: boolean
  status: VStatus
  /** Checked = included in the next Save. Owned rows default true, unowned false. */
  selected: boolean
  /** Loaded original values, for the dirty check. */
  orig?: { price: string; qty: string; sku: string }
  /** Last API error for this row. */
  err?: string
}

const toNum = (v: string): number => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

const VariantMultiOfferForm: FC<{
  barcode: string
  variants: ICatalogVariantPreview[]
  onDone?: () => void
  /** Bulk-sheet seed — pre-fills new (unowned) variant rows' price/qty/sku so a
   *  bulk upload lands ready to save. Owned rows still load their live offer. */
  seed?: { price?: string; quantity?: string; supplierSku?: string }
  /** Images from the uploaded bulk image-zip, matched to this barcode —
   *  pre-fills the "add new variant" image picker (new-colour case). */
  seedImages?: File[]
  /** Hide the internal banner + product header when embedded in a card that
   *  already renders them (e.g. the bulk-processing results card). */
  hideHeader?: boolean
  /** Bulk UPDATE (edit) resolution: seed OWNED variant rows from the sheet
   *  `seed` (price/qty/sku) and skip the live-offer fetch, so the supplier can
   *  apply the sheet's intended values to the picked variant via updateOffer.
   *  Rows default UNSELECTED so the supplier explicitly picks which duplicated
   *  variant(s) to update. Defaults false → add / offers behavior is unchanged. */
  seedOwnedRows?: boolean
  /** Whether owned variant rows pre-fill their live offer's price/qty.
   *  Defaults to true (offers / bulk pages). The add-single-product page
   *  passes false so the supplier types fresh price/qty (SKU still loads). */
  prefillOfferValues?: boolean
}> = ({ barcode, variants, onDone, seed, seedImages, hideHeader, seedOwnedRows = false, prefillOfferValues = true }) => {
  const { formatMessage } = useIntl()
  const { currentUser, language } = SharedData()
  const token = currentUser?.token

  const apiErr = (e: any): string => {
    const code = e?.response?.data?.error
    return code
      ? formatMessage({ id: `offers.form.api_err.${code}`, defaultMessage: code })
      : formatMessage({ id: 'something_wrong_desc' })
  }

  const defaultRow = (v: ICatalogVariantPreview): VRow => {
    const owned = Boolean(v.has_my_offer)
    // Update mode seeds owned rows too (from the sheet) and skips the live-offer
    // fetch; add / offers mode seeds only new (unowned) rows and loads owned
    // rows' live offer in the effect below.
    const applySeed = seedOwnedRows || !owned
    return {
      price: applySeed && seed?.price ? seed.price : '',
      qty: applySeed && seed?.quantity ? seed.quantity : '',
      sku: applySeed && seed?.supplierSku ? seed.supplierSku : '',
      psid: v.product_supplier_id ?? null,
      owned,
      loading: Boolean(!seedOwnedRows && owned && v.product_supplier_id != null),
      status: 'idle',
      // Add / offers: owned rows default checked (supplier likely wants to
      // update), unowned unchecked. Update mode: everything defaults unchecked so
      // the supplier explicitly picks which duplicated variant(s) to update.
      selected: seedOwnedRows ? false : owned,
    }
  }

  const [rows, setRows] = useState<Record<number, VRow>>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const rowOf = (v: ICatalogVariantPreview): VRow => rows[v.product_id] ?? defaultRow(v)
  const patchRow = (v: ICatalogVariantPreview, patch: Partial<VRow>) =>
    setRows((prev) => ({ ...prev, [v.product_id]: { ...(prev[v.product_id] ?? defaultRow(v)), ...patch } }))

  // Pre-fill owned variants with their live offer values (price/qty/sku/cost).
  // The lookup only carries product_supplier_id, so read each offer once.
  useEffect(() => {
    // Update mode: owned rows are seeded from the sheet (not the live offer) and
    // already carry their product_supplier_id — skip the fetch so the seeded
    // values stand and no `orig` is set (which keeps the row actionable).
    if (seedOwnedRows) return
    let cancelled = false
    const owned = variants.filter((v) => v.has_my_offer && v.product_supplier_id != null)
    if (owned.length === 0) return
    void Promise.all(
      owned.map(async (v) => {
        try {
          const res = await getOffer(v.product_supplier_id as number, token, language)
          const o: any = res.data.data
          if (cancelled) return
          const loaded = {
            price: o.price != null ? String(parseFloat(o.price)) : '',
            qty: o.supplier_quantity != null ? String(o.supplier_quantity) : (o.quantity != null ? String(o.quantity) : ''),
            sku: o.supplier_sku ?? '',
          }
          if (prefillOfferValues) {
            patchRow(v, { ...loaded, orig: loaded, loading: false })
          } else {
            // add-single-product page: don't seed price/qty from the live offer
            // — the supplier types fresh values. SKU still loads; we omit
            // `orig` so a typed price marks the owned row actionable (no
            // dirty-vs-original gate).
            patchRow(v, { price: '', qty: '', sku: loaded.sku, loading: false })
          }
        } catch {
          if (!cancelled) patchRow(v, { loading: false })
        }
      }),
    )
    return () => {
      cancelled = true
    }
    // variants/token/language are stable for a given lookup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode])

  const ownedAtLookup = useMemo(() => variants.filter((v) => v.has_my_offer).length, [variants])

  // Existing variant colours — a colour the supplier picks that isn't here is a
  // NEW colour and the add-variant panel makes its image required.
  const existingColorIds = useMemo(
    () => Array.from(new Set(variants.map((v) => v.color_id).filter((id) => id > 0))),
    [variants],
  )

  const label = (v: ICatalogVariantPreview) =>
    [v.size_label, v.color_label].filter(Boolean).join(' / ') || v.name || `#${v.product_id}`

  const isDirty = (r: VRow) =>
    Boolean(r.orig) && (r.price !== r.orig!.price || r.qty !== r.orig!.qty || r.sku !== r.orig!.sku)

  // Rows that will be saved on submit: only checked rows that also have content.
  const actionable = useMemo(
    () =>
      variants.filter((v) => {
        const r = rowOf(v)
        if (!r.selected) return false
        if (r.loading || r.status === 'saving') return false
        if (r.owned) return r.orig ? isDirty(r) : r.psid != null && r.price.trim() !== ''
        return r.price.trim() !== ''
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variants, rows],
  )

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (actionable.length === 0) {
      setError(formatMessage({ id: 'offers.variants.none_selected' }))
      return
    }
    // Validate every row before sending any.
    for (const v of actionable) {
      const r = rowOf(v)
      const p = toNum(r.price)
      const q = parseInt(r.qty, 10)
      if (!Number.isFinite(p) || p <= 0) return setError(formatMessage({ id: 'offers.form.err.price_invalid' }))
      if (!Number.isFinite(q) || q < 0) return setError(formatMessage({ id: 'offers.form.err.qty_invalid' }))
      if ((r.sku ?? '').length > 64) return setError(formatMessage({ id: 'offers.form.err.sku_too_long' }))
    }
    setError(null)
    setSubmitting(true)
    let savedAny = false
    let firstErr: string | null = null
    for (const v of actionable) {
      const r = rowOf(v)
      patchRow(v, { status: 'saving', err: undefined })
      const sku = r.sku.trim() === '' ? null : r.sku.trim()
      const cur = { price: r.price, qty: r.qty, sku: r.sku }
      try {
        if (r.owned && r.psid != null) {
          const payload: IOfferUpdatePayload = {
            price: toNum(r.price),
            supplier_quantity: parseInt(r.qty, 10),
            supplier_sku: sku,
          }
          await updateOffer(r.psid, payload, token, language)
          patchRow(v, { status: 'done', orig: cur })
        } else {
          const payload: IFromBarcodePayload = {
            barcode,
            product_id: v.product_id,
            price: toNum(r.price),
            quantity: parseInt(r.qty, 10),
            supplier_sku: sku,
          }
          await createOfferFromBarcode(payload, token, language)
          patchRow(v, { status: 'done', owned: true, orig: cur })
        }
        savedAny = true
      } catch (err: any) {
        // 409 already_have_offer (race) → it exists, treat as saved/active.
        if (err?.response?.status === 409) {
          patchRow(v, { status: 'done', owned: true, orig: cur })
          savedAny = true
        } else {
          const msg = apiErr(err)
          patchRow(v, { status: 'error', err: msg })
          if (!firstErr) firstErr = msg
        }
      }
    }
    setSubmitting(false)
    if (firstErr) setError(firstErr)
    if (savedAny) onDone?.()
  }

  // Variants of one master share the product's image / name / brand.
  const head = variants[0]


  const statusCell = (r: VRow) => {
    if (r.loading || r.status === 'saving') return <span className='vmof-spinner' aria-hidden='true' />
    if (r.status === 'error')
      return (
        <span className='vmof-badge vmof-badge--error' title={r.err}>
          {formatMessage({ id: 'offers.variants.error_badge' })}
        </span>
      )
    if (r.owned || r.status === 'done')
      return <span className='vmof-badge vmof-badge--active'>{formatMessage({ id: 'vmof_active' })}</span>
    return <span className='vmof-badge vmof-badge--none'>{formatMessage({ id: 'vmof_no_offer' })}</span>
  }

  return (
    <form onSubmit={submit}>
      {!hideHeader && (
        <>
          <div className='vmof-banner'>
            <i className='fas fa-circle-info' aria-hidden='true' />
            <span>{formatMessage({ id: ownedAtLookup > 0 ? 'vmof_banner_update' : 'vmof_banner_create' })}</span>
          </div>

          {/* Product header */}
          <div className='vmof-product'>
            {head?.image ? (
              <img
                className='vmof-thumb'
                src={toCatalogImageUrl(head.image)}
                alt=''
                loading='lazy'
                onError={(e) => {
                  // Catalog image URL can 404 — fall back to the placeholder so
                  // the header shows a clean thumbnail, not a broken-image icon.
                  const img = e.currentTarget
                  if (img.dataset.fallback !== '1') {
                    img.dataset.fallback = '1'
                    img.src = toAbsoluteUrl('/media/logos/img-placeholder.png')
                  }
                }}
              />
            ) : null}
            <div className='vmof-product-info'>
              <div className='vmof-product-name'>{head?.name ?? '—'}</div>
              {head?.brand ? (
                <div className='vmof-product-meta'>
                  {formatMessage({ id: 'offers.from_barcode.brand_line' }, { brand: head.brand })}
                </div>
              ) : null}
            </div>
            {ownedAtLookup > 0 ? (
              <Link to={`/offers/list?search=${encodeURIComponent(barcode)}`} className='vmof-view-live'>
                <i className='fas fa-eye' aria-hidden='true' />
                {formatMessage({ id: 'vmof_view_live' })}
              </Link>
            ) : null}
          </div>
        </>
      )}

      {/* Variant table */}
      <div className='vmof-table-wrap'>
        <table className='vmof-table'>
          <thead>
            <tr>
              <th className='vmof-th-check' />
              <th>{formatMessage({ id: 'vmof_size_color' })}</th>
              <th>{formatMessage({ id: 'offers.form.price' })}</th>
              <th>{formatMessage({ id: 'offers.form.quantity' })}</th>
              <th>{formatMessage({ id: 'offers.form.supplier_sku' })}</th>
              <th>{formatMessage({ id: 'vmof_status' })}</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => {
              const r = rowOf(v)
              const disabled = submitting || r.loading || r.status === 'saving'
              return (
                <tr key={v.product_id} className={r.selected ? '' : 'vmof-row-unchecked'}>
                  <td className='vmof-td-check'>
                    <input
                      type='checkbox'
                      className='vmof-checkbox'
                      checked={r.selected ?? Boolean(v.has_my_offer)}
                      onChange={(e) => patchRow(v, { selected: e.target.checked })}
                      disabled={submitting || r.status === 'saving'}
                      aria-label={`Select ${label(v)}`}
                    />
                  </td>
                  <td className='vmof-variant-label'>{label(v)}</td>
                  <td>
                    <input
                      type='number'
                      step='0.01'
                      min='0.01'
                      className='vmof-input'
                      placeholder='0.00'
                      value={r.price}
                      onChange={(e) => patchRow(v, { price: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                  <td>
                    <input
                      type='number'
                      min='0'
                      className='vmof-input vmof-input--qty'
                      placeholder='0'
                      value={r.qty}
                      onChange={(e) => patchRow(v, { qty: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                  <td>
                    <input
                      type='text'
                      maxLength={64}
                      className='vmof-input vmof-input--sku'
                      placeholder={formatMessage({ id: 'optional' })}
                      value={r.sku}
                      onChange={(e) => patchRow(v, { sku: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                  <td>{statusCell(r)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {error ? <div className='vmof-error'>{error}</div> : null}

      <div className='vmof-actions'>
        <button type='submit' className='vmof-submit' disabled={submitting || actionable.length === 0}>
          {submitting ? formatMessage({ id: 'vmof_saving' }) : formatMessage({ id: 'vmof_save' })}
        </button>
      </div>

      {/* Add a NEW size/color not in the catalog yet — always available,
          even when the supplier already sells every listed variant. Files
          a catalog request (pending), not an instant offer. */}
      <AddVariantsPanel barcode={barcode} seedImages={seedImages} existingColorIds={existingColorIds} />
    </form>
  )
}

export default VariantMultiOfferForm
